/**
 * REST client for the FastAPI backend.
 *
 * Replaces the old gapi/Google Sheets access. The catalog payload the backend
 * returns matches the shapes the rest of the app already uses.
 */

import type { SessionProfile } from './session';

// Runtime configuration injected by the container (chart ConfigMap). Falls back
// to build-time VITE_API_BASE_URL, then to the local dev backend.
type CashierRuntimeConfig = {
  /** Base path the SPA is served under, e.g. "/" or "/Cashier-App/". */
  basePath?: string;
  /** Origin/base the API is called on. "/" means same-origin. */
  apiBaseUrl?: string;
};

const runtimeConfig: CashierRuntimeConfig = (typeof window !== 'undefined' ? window.__CASHIER_CONFIG__ : undefined) ?? {};

/**
 * Work out the path the SPA is served under, without a rebuild.
 *
 * `config.js` may omit `basePath` (the container leaves it unset), so this falls
 * back to reading it off the built module script's own URL — Vite rewrites that
 * tag with the base it built for. That keeps one bundle usable both at the root
 * and under `/Cashier-App/`.
 *
 * Deliberately not `import.meta.env.BASE_URL`: that is fixed at build time, so
 * it cannot adapt to how the image actually gets hosted.
 */
function detectBasePath(): string {
  if (typeof document === 'undefined') return '/';
  const moduleScript = Array.from(document.scripts).find((script) => script.type === 'module' && script.src.includes('/assets/'));
  if (moduleScript) {
    const index = moduleScript.src.indexOf('/assets/');
    try {
      return new URL(moduleScript.src.slice(0, index + 1)).pathname;
    } catch {
      // Fall through to the default below.
    }
  }
  return '/';
}

// Normalise to a trailing-slash form ("/" or "/Cashier-App/").
const BASE_PATH = (() => {
  const source = runtimeConfig.basePath ?? detectBasePath();
  const trimmed = source.replace(/^\/+/, '').replace(/\/+$/, '');
  return trimmed ? `/${trimmed}/` : '/';
})();
// Prefix for site-relative assets stored in the catalog (e.g. "/maomao.png").
const SITE_PREFIX = BASE_PATH === '/' ? '' : BASE_PATH.replace(/\/$/, '');

// 127.0.0.1 rather than localhost: uvicorn binds IPv4 by default, while Node and
// some browsers resolve `localhost` to ::1 first. Set VITE_API_BASE_URL to point
// somewhere else. The empty string means same-origin, which is what the
// container uses so the ingress can route /api itself.
const API_BASE_URL = (() => {
  const configured =
    runtimeConfig.apiBaseUrl === '/' ? '' : (runtimeConfig.apiBaseUrl ?? import.meta.env.VITE_API_BASE_URL ?? 'http://127.0.0.1:8000');
  return configured.replace(/\/+$/, '');
})();

/**
 * Absolute URL for an image shown in an `<img>`.
 *
 * Catalog rows carry two kinds of value: legacy site-relative paths that the
 * SPA already serves (e.g. `/Cashier-App/maomao.png`, resolved against the
 * origin) and backend-owned API paths (e.g. `/api/images/abc?v=1`, resolved
 * against the API). `API_BASE_URL` is usually a different origin in dev, so the
 * two must not be mixed up.
 */
export function resolveImageSrc(img: string): string {
  if (!img) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(img) || img.startsWith('//')) return img;
  if (img.startsWith('/api/')) return `${API_BASE_URL}${img}`;
  return `${SITE_PREFIX}${img}`;
}

export class ApiError extends Error {
  /** HTTP status, or 0 when the server could not be reached at all. */
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export type Payment = 'cash' | 'paynow';

export type SaleReceipt = {
  id: number;
  order_ref: string;
  payment: Payment;
  total: number;
  /** Bundle saving already taken off total. */
  discount: number;
  discount_label: string;
  created_at: string;
  items: { item_id: number | null; title: string; price: number; quantity: number; subtotal: number }[];
  /** Booth event this sale belongs to. Null shows as "Unassigned", never hidden. */
  event_id: number | null;
  event_name: string | null;
};

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === 'object' && 'detail' in body) {
      const { detail } = body as { detail: unknown };
      if (typeof detail === 'string') return detail;
      // FastAPI validation errors arrive as a list of {loc, msg, ...}
      if (Array.isArray(detail)) {
        return detail
          .map((entry) => (entry && typeof entry === 'object' && 'msg' in entry ? String((entry as { msg: unknown }).msg) : String(entry)))
          .join('; ');
      }
    }
  } catch {
    // fall through to the generic message
  }
  return `Request failed (HTTP ${response.status})`;
}

/** The non-HttpOnly half of the double-submit CSRF pair; matches the backend. */
const CSRF_COOKIE = 'cashier_csrf';
const CSRF_HEADER = 'X-CSRF-Token';

/** Read a cookie by name; the CSRF value is deliberately readable by script. */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return null;
}

async function request<T>(path: string, init: RequestInit = {}, signal?: AbortSignal): Promise<T> {
  const headers = new Headers(init.headers);
  // FormData must keep the browser's own Content-Type: it carries the multipart
  // boundary. Forcing JSON here silently breaks image uploads.
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (init.body && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  // The session rides in the HttpOnly cookie, so every request must include
  // credentials: outside production the API is a different origin and a
  // same-origin fetch would drop both the cookie and any Set-Cookie. The
  // state-changing verbs also echo the CSRF half-token; SameSite=Strict is the
  // primary defence and this is the depth behind it.
  const method = (init.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers.set(CSRF_HEADER, csrf);
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, { credentials: 'include', ...init, headers, signal: signal ?? init.signal });
  } catch (err) {
    // A cancellation is the caller's own abort, not a reachability failure, so
    // it must pass through untouched rather than become an ApiError.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError('Cannot reach the server. Is the backend running?', 0);
  }

  if (!response.ok) {
    throw new ApiError(await readErrorMessage(response), response.status);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/** The non-secret half of a successful sign-in response. */
export type AuthSession = SessionProfile & {
  /** Seconds until the cookie and the JWT behind it expire, from the server. */
  expires_in: number;
};

type TokenResponse = AuthSession & { access_token: string; token_type: string };

/**
 * Keep the non-secret session the UI needs and drop the credential.
 *
 * The JWT is already in the response's HttpOnly cookie; returning it to script
 * would undo the migration. The profile is what cart keys, the sign-in prompt
 * and admin gating use from here on.
 */
function sessionFromResponse(result: TokenResponse): AuthSession {
  return { username: result.username, is_admin: result.is_admin, expires_in: result.expires_in };
}

/** OAuth2 password flow: form-encoded credentials in, a cookie session out. */
export async function login(username: string, password: string): Promise<AuthSession> {
  const body = new URLSearchParams({ username, password });
  const result = await request<TokenResponse>('/api/auth/token', {
    method: 'POST',
    body,
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    // The sign-in response sets the session cookie and the customer display's
    // stream cookie. Credentials have to be included or a cross-origin
    // Set-Cookie is dropped.
    credentials: 'include',
  });
  return sessionFromResponse(result);
}

/**
 * Create an account and sign in.
 *
 * The password is sent once; the "type it twice" check is a client concern.
 * The server answers with a session, so a new account is usable immediately.
 */
export async function register(username: string, password: string): Promise<AuthSession> {
  const result = await request<TokenResponse>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  return sessionFromResponse(result);
}

/** Which sign-in methods this deployment offers. */
export type AuthMethods = {
  password: boolean;
  google: boolean;
};

export function fetchAuthMethods(): Promise<AuthMethods> {
  return request<AuthMethods>('/api/auth/methods');
}

/**
 * Absolute URL that begins the Google redirect flow.
 *
 * A full navigation, not a fetch: the browser must be able to follow the 302 to
 * Google and come back with the callback's state cookie.
 */
export function googleSignInUrl(): string {
  return `${API_BASE_URL}/api/auth/google/start`;
}

/** Trade the one-time code from the Google callback for a normal session. */
export async function exchangeGoogleCode(code: string): Promise<AuthSession> {
  const result = await request<TokenResponse>('/api/auth/google/exchange', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  return sessionFromResponse(result);
}

/**
 * End the cookie session.
 *
 * The JWT is stateless and the cookies are HttpOnly, so only the server can
 * clear them; a client-side delete would leave the credential readable and the
 * session live. Best-effort: the caller drops local state either way.
 */
export function endSession(): Promise<void> {
  return request<void>('/api/auth/logout', { method: 'POST', credentials: 'include' });
}

export function fetchCatalog(): Promise<CategoryItem[]> {
  return request<CategoryItem[]>('/api/catalog', {});
}

/** Where a bundle requirement group takes its eligible items from. */
export type BundleSource = 'category' | 'items' | 'tag';

/**
 * One "choose N from ..." requirement group, as the till API serves it.
 *
 * The server resolves `item_ids` for every source, so the till can match a
 * bundle offline with no extra catalogue lookups.
 */
export type CatalogBundleGroup = {
  /** The till payload names the source `kind`; `source` is accepted too. */
  kind?: BundleSource;
  source?: BundleSource;
  quantity: number;
  category_id: number | null;
  tag_id: number | null;
  item_ids: number[];
};

/** A bundle the cashier can ring up: one price for a group requirement. */
export type CatalogBundle = {
  id: number;
  name: string;
  price: number;
  groups: CatalogBundleGroup[];
};

/** Active bundles for the till, in display order. */
export function fetchBundles(): Promise<CatalogBundle[]> {
  return request<CatalogBundle[]>('/api/bundles', {});
}

// ------------------------------------------------------------------ CMS / admin

export type AdminItem = {
  id: number;
  category_id: number;
  title: string;
  /** Integer cents, the storage unit. Divide by 100 only for display. */
  price_cents: number;
  img: string;
  image_id: number | null;
  image_url: string | null;
  sort_order: number;
  /** Off the till, but still in the catalogue and in past sales. */
  is_hidden: boolean;
  /** Tags this item carries; tags group items for any-N-for-P bundles. */
  tag_ids: number[];
};

export type AdminCategory = {
  id: number;
  name: string;
  palette1: string;
  palette2: string;
  palette3: string;
  sort_order: number;
  items: AdminItem[];
};

export type UploadedImage = {
  id: number;
  url: string;
  width: number;
  height: number;
  byte_size: number;
  content_type: string;
  original_filename: string;
};

/** Normalised crop rectangle, each value 0..1 of the oriented source image. */
export type CropBox = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export function fetchAdminCatalog(): Promise<AdminCategory[]> {
  return request<AdminCategory[]>('/api/admin/catalog', {});
}

export function createCategory(name: string): Promise<AdminCategory> {
  return request<AdminCategory>('/api/admin/categories', { method: 'POST', body: JSON.stringify({ name }) });
}

export function updateCategory(
  categoryId: number,
  patch: Partial<Pick<AdminCategory, 'name' | 'palette1' | 'palette2' | 'palette3' | 'sort_order'>>
): Promise<AdminCategory> {
  return request<AdminCategory>(`/api/admin/categories/${categoryId}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export function deleteCategory(categoryId: number): Promise<void> {
  return request<void>(`/api/admin/categories/${categoryId}`, { method: 'DELETE' });
}

/**
 * Replace the menu order of every category.
 *
 * Sends the complete ordered id list rather than a per-category `sort_order` so
 * a reorder is one atomic request; the server rejects a list that does not name
 * every category exactly once.
 */
export function reorderCategories(ids: number[]): Promise<AdminCategory[]> {
  return request<AdminCategory[]>('/api/admin/categories/order', { method: 'PUT', body: JSON.stringify({ ids }) });
}

export function createItem(payload: {
  category_id: number;
  title: string;
  price_cents: number;
  image_id?: number | null;
  is_hidden?: boolean;
  tag_ids?: number[];
}): Promise<AdminItem> {
  return request<AdminItem>('/api/admin/items', { method: 'POST', body: JSON.stringify(payload) });
}

export function updateItem(
  itemId: number,
  patch: Partial<{
    category_id: number;
    title: string;
    price_cents: number;
    image_id: number | null;
    img: string;
    sort_order: number;
    is_hidden: boolean;
    tag_ids: number[];
  }>
): Promise<AdminItem> {
  return request<AdminItem>(`/api/admin/items/${itemId}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export function deleteItem(itemId: number): Promise<void> {
  return request<void>(`/api/admin/items/${itemId}`, { method: 'DELETE' });
}

// ------------------------------------------------------------- bundles (discounts)

/** One eligible item inside an admin bundle group, with its catalog details. */
export type AdminBundleGroupItem = {
  item_id: number;
  title: string;
  price_cents: number;
  img: string;
  is_hidden: boolean;
};

/** One "choose N from ..." requirement group, as the admin API serves it. */
export type AdminBundleGroup = {
  id: number;
  quantity: number;
  source: BundleSource;
  category_id: number | null;
  category_name: string | null;
  tag_id: number | null;
  tag_name: string | null;
  /** For a SELECTED_ITEMS group: the chosen member ids. */
  item_ids: number[];
  /** How many items are currently eligible for this group. */
  item_count: number;
  /** Cheapest pick of `quantity` eligible units at current prices. */
  min_cents: number;
  /** Dearest pick of `quantity` eligible units at current prices. */
  max_cents: number;
  items: AdminBundleGroupItem[];
};

export type AdminBundle = {
  id: number;
  name: string;
  price_cents: number;
  active: boolean;
  sort_order: number;
  groups: AdminBundleGroup[];
  /** Cheapest total across every group, so the UI can show the saving. */
  components_total_cents: number;
  /** Dearest total across every group; the bundle price may not exceed it. */
  components_max_cents: number;
};

/** A requirement group on the wire: source plus its one target. */
export type BundleGroupInput = {
  quantity: number;
  source: BundleSource;
  category_id?: number | null;
  tag_id?: number | null;
  item_ids?: number[];
};

export type BundleInput = {
  name: string;
  price_cents: number;
  active?: boolean;
  sort_order?: number;
  groups: BundleGroupInput[];
};

export function fetchAdminBundles(): Promise<AdminBundle[]> {
  return request<AdminBundle[]>('/api/admin/bundles', {});
}

export function createBundle(payload: BundleInput): Promise<AdminBundle> {
  return request<AdminBundle>('/api/admin/bundles', { method: 'POST', body: JSON.stringify(payload) });
}

export function updateBundle(bundleId: number, patch: Partial<BundleInput>): Promise<AdminBundle> {
  return request<AdminBundle>(`/api/admin/bundles/${bundleId}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export function deleteBundle(bundleId: number): Promise<void> {
  return request<void>(`/api/admin/bundles/${bundleId}`, { method: 'DELETE' });
}

// ------------------------------------------------------------------ tags

export type Tag = {
  id: number;
  name: string;
  colour: string;
  sort_order: number;
  item_count: number;
};

export function fetchTags(): Promise<Tag[]> {
  return request<Tag[]>('/api/admin/tags', {});
}

export function createTag(payload: { name: string; colour?: string; sort_order?: number }): Promise<Tag> {
  return request<Tag>('/api/admin/tags', { method: 'POST', body: JSON.stringify(payload) });
}

export function updateTag(tagId: number, patch: Partial<{ name: string; colour: string; sort_order: number }>): Promise<Tag> {
  return request<Tag>('/api/admin/tags/' + tagId, { method: 'PATCH', body: JSON.stringify(patch) });
}

export function deleteTag(tagId: number): Promise<void> {
  return request<void>('/api/admin/tags/' + tagId, { method: 'DELETE' });
}

// ------------------------------------------------------------ users (admin)

export type AdminUser = {
  id: number;
  username: string;
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
  /** Row counts shown before a destructive delete. */
  sales: number;
  items: number;
  events: number;
};

export function fetchAdminUsers(): Promise<AdminUser[]> {
  return request<AdminUser[]>('/api/admin/users', {});
}

/** Delete an account and everything it owns. Admin only. */
export function deleteUser(userId: number): Promise<void> {
  return request<void>(`/api/admin/users/${userId}`, { method: 'DELETE' });
}

/**
 * Upload an image, optionally cropped to a square.
 *
 * The crop is sent as normalised coordinates rather than cropped pixels so the
 * server can apply it to the full-resolution original. The backend re-derives a
 * square from these regardless of what is sent.
 */
export async function uploadItemImage(file: File, crop?: CropBox | null): Promise<UploadedImage> {
  const form = new FormData();
  form.append('file', file);
  if (crop) {
    form.append('crop_left', String(crop.left));
    form.append('crop_top', String(crop.top));
    form.append('crop_right', String(crop.right));
    form.append('crop_bottom', String(crop.bottom));
  }
  // No explicit Content-Type: the browser must add the multipart boundary.
  return request<UploadedImage>('/api/images', { method: 'POST', body: form });
}

/**
 * Record a sale.
 *
 * The client sends only ids and quantities, never prices: a bundle is priced
 * and expanded server-side exactly like a catalog item, so the till cannot
 * choose what the customer pays. `clientRef` is the idempotency key and should
 * be supplied whenever the caller might retry — a lost response is
 * indistinguishable from a failure, so without it a retry bills twice.
 */
export type RecordSaleInput = {
  payment: Payment;
  items: { item_id: number; quantity: number }[];
  /** Bundles rung up by id; the server expands and prices them. */
  bundles?: { bundle_id: number; quantity: number }[];
  /** Idempotency key; see the note above. */
  clientRef?: string;
  /** The booth event the sale is attributed to. */
  eventId?: number | null;
};

export function recordSale(sale: RecordSaleInput): Promise<SaleReceipt> {
  const body: Record<string, unknown> = { payment: sale.payment, items: sale.items };
  if (sale.bundles && sale.bundles.length > 0) body.bundles = sale.bundles;
  if (sale.clientRef) body.client_ref = sale.clientRef;
  if (sale.eventId != null) body.event_id = sale.eventId;
  return request<SaleReceipt>('/api/sales', { method: 'POST', body: JSON.stringify(body) });
}

// -------------------------------------------------------------- offline sync

export type SyncSaleLine = {
  item_id: number;
  title: string;
  price_cents: number;
  quantity: number;
  subtotal_cents: number;
};

export type SyncSale = {
  client_ref: string;
  payment: Payment;
  sold_at: string;
  catalog_revision: number | null;
  /**
   * Event captured at sale time. Optional because entries queued before events
   * existed are still in some devices' storage and must keep syncing.
   */
  event_id?: number | null;
  /** Bundle saving the device actually gave; the server records it clamped. */
  discount_cents: number;
  discount_label: string;
  items: SyncSaleLine[];
};

export type SyncSaleResult = {
  client_ref: string;
  status: 'recorded' | 'duplicate' | 'rejected';
  sale_id: number | null;
  order_ref: string | null;
  price_conflict: boolean;
  reason: string | null;
};

export type SyncBatchResult = {
  results: SyncSaleResult[];
  recorded: number;
  duplicates: number;
  rejected: number;
  conflicts: number;
};

/** Reconcile sales rung up offline. Idempotent per `client_ref` on the server. */
export function syncSales(sales: SyncSale[]): Promise<SyncBatchResult> {
  return request<SyncBatchResult>('/api/sales/sync', { method: 'POST', body: JSON.stringify({ sales }) });
}

export type CatalogRevision = { revision: number };

export function fetchCatalogRevision(): Promise<CatalogRevision> {
  return request<CatalogRevision>('/api/catalog/revision', {});
}

/**
 * Is the backend reachable right now?
 *
 * Deliberately not `navigator.onLine`: that reports whether a network interface
 * exists, not whether the API can be reached, and it is wrong behind captive
 * portals and VPNs — it would claim "online" while every sale fails.
 */
export async function probeBackend(timeoutMs = 4000): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${API_BASE_URL}/api/health`, {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

// -------------------------------------------------------------------- events

export type BoothEvent = {
  id: number;
  name: string;
  /** ISO calendar date (YYYY-MM-DD). */
  start_date: string;
  end_date: string | null;
  venue: string;
  notes: string;
  is_archived: boolean;
  created_at: string;
};

export type EventStats = {
  revenue: number;
  transactions: number;
  items_sold: number;
  average_sale: number;
  first_sale_at: string | null;
  last_sale_at: string | null;
};

/** An event plus its rolled-up sales, as the events page lists it. */
export type EventSummary = BoothEvent & { stats: EventStats };

export type EventsOverview = {
  events: EventSummary[];
  /** Sales with no event, so the page's totals always reconcile. */
  unassigned: EventStats;
};

export type EventInput = {
  name: string;
  start_date: string;
  end_date?: string | null;
  venue?: string;
  notes?: string;
};

export function fetchEvents(includeArchived = false): Promise<EventsOverview> {
  const query = includeArchived ? '?include_archived=true' : '';
  return request<EventsOverview>(`/api/events${query}`, {});
}

export function createEvent(payload: EventInput): Promise<BoothEvent> {
  return request<BoothEvent>('/api/events', { method: 'POST', body: JSON.stringify(payload) });
}

export function updateEvent(eventId: number, patch: Partial<EventInput> & { is_archived?: boolean }): Promise<BoothEvent> {
  return request<BoothEvent>(`/api/events/${eventId}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export function deleteEvent(eventId: number): Promise<void> {
  return request<void>(`/api/events/${eventId}`, { method: 'DELETE' });
}

// ------------------------------------------------------------------- reporting

export type PeriodTotals = {
  revenue: number;
  transactions: number;
  items_sold: number;
  average_sale: number;
};

export type DailyTotal = {
  /** ISO calendar date (YYYY-MM-DD). */
  date: string;
  revenue: number;
  transactions: number;
  /** Units sold that day; additive, used for the previous-window comparison. */
  items_sold: number;
};

export type PaymentSplit = {
  payment: Payment;
  revenue: number;
  transactions: number;
  /** Fraction of period revenue, 0..1. */
  share: number;
};

export type TopItem = {
  title: string;
  quantity: number;
  revenue: number;
};

export type SalesStats = {
  range: { days: number; start: string; end: string };
  today: PeriodTotals;
  period: PeriodTotals;
  /** Bundle savings granted in the period; period revenue is already net. */
  discounts: number;
  daily: DailyTotal[];
  payments: PaymentSplit[];
  /** Most units first, for the default ranking. */
  top_items: TopItem[];
  /**
   * Most net revenue first. Both rankings are aggregated over every item in the
   * period server-side, so the page can switch between them without re-ranking a
   * truncated list.
   */
  items_by_revenue: TopItem[];
  /** Sales that used a bundle offer, their takings, and the savings given. */
  bundle_transactions: number;
  bundle_revenue: number;
  bundle_offers: { label: string; sales: number }[];
};

/**
 * The transaction table's own filters.
 *
 * Kept separate from SalesFilter because they narrow the table rather than the
 * report: sent to the server so they cover the whole history, not just the page
 * that happens to be loaded.
 */
export type SalesListFilter = SalesFilter & { payment?: string; q?: string };

/**
 * Which slice of sales another page wants the report to open on.
 *
 * \`eventId\` and \`unassigned\` are mutually exclusive, matching the API.
 */
export type SalesFocus = { eventId?: number; unassigned?: boolean };

export type SalesFilter = {
  /** Rolling window in days. Ignored for a single event, which uses its own span. */
  days?: number;
  /** Restrict to one booth event. */
  eventId?: number;
  /** Restrict to sales with no event. Mutually exclusive with eventId. */
  unassigned?: boolean;
};

/** The params the list, stats and export endpoints share. */
function eventScopeParams(filter: SalesFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filter.eventId !== undefined) params.set('event_id', String(filter.eventId));
  if (filter.unassigned) params.set('unassigned', 'true');
  return params;
}

/**
 * One query string for every sales endpoint.
 *
 * Shared so the chart, the table and the CSV always scope themselves
 * identically — a filter that reached one but not another would make an export
 * disagree with the screen it was downloaded from.
 */
function salesFilterQuery(filter: SalesFilter = {}): string {
  const params = eventScopeParams(filter);
  if (filter.days !== undefined) params.set('days', String(filter.days));
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function fetchSalesStats(filter: SalesFilter = {}): Promise<SalesStats> {
  return request<SalesStats>(`/api/sales/stats${salesFilterQuery(filter)}`, {});
}

export function fetchRecentSales(limit: number, offset: number, filter: SalesListFilter = {}): Promise<SaleReceipt[]> {
  const params = eventScopeParams(filter);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  if (filter.payment) params.set('payment', filter.payment);
  if (filter.q) params.set('q', filter.q);
  return request<SaleReceipt[]>(`/api/sales?${params.toString()}`, {});
}

export type DeletedSale = {
  id: number;
  sale_id: number;
  order_ref: string;
  total: number;
  payment: string;
  /** Which event the removed sale belonged to, snapshotted by name. */
  event_id: number | null;
  event_name: string;
  reason: string;
  deleted_by: string | null;
  deleted_at: string;
};

/**
 * Delete a recorded sale.
 *
 * The server requires a reason and keeps an audit record, so this is not a
 * silent removal — see `app/routers/sales.py`.
 */
export function deleteSale(saleId: number, reason: string): Promise<DeletedSale> {
  return request<DeletedSale>(`/api/sales/${saleId}`, { method: 'DELETE', body: JSON.stringify({ reason }) });
}

export function fetchDeletedSales(limit = 50): Promise<DeletedSale[]> {
  return request<DeletedSale[]>(`/api/sales/deletions?limit=${limit}`, {});
}

export type SaleAssignResult = {
  /** How many sales moved; zero means there were none left to move. */
  assigned: number;
  event_id: number;
};

/**
 * Attribute every sale that has no event to one event.
 *
 * Only sales with no event are touched, so this is safe to run twice and can
 * never steal a sale that already has an attribution.
 */
export function assignUnassignedSales(eventId: number): Promise<SaleAssignResult> {
  return request<SaleAssignResult>('/api/sales/assign', { method: 'POST', body: JSON.stringify({ event_id: eventId }) });
}

/**
 * Download the sales CSV for the current filter.
 *
 * The endpoint needs the cookie session, which a plain `<a href>` download
 * cannot send, so the file is fetched and handed to the browser as a blob. The server
 * names the file; its suggestion is preferred over anything invented here.
 */
export async function downloadSalesCsv(filter: SalesFilter = {}): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/sales/export.csv${salesFilterQuery(filter)}`, { credentials: 'include' });
  } catch {
    throw new ApiError('Cannot reach the server. Is the backend running?', 0);
  }
  if (!response.ok) {
    throw new ApiError(await readErrorMessage(response), response.status);
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? `cashier-sales-${new Date().toISOString().slice(0, 10)}.csv`;

  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// ----------------------------------------------------------- customer display

export type DisplayLine = {
  id: number | null;
  title: string;
  quantity: number;
  price: number;
  subtotal: number;
};

/** A ready-to-render PayNow QR for the basket's current total. */
export type DisplayPaynow = {
  /** The EMVCo payload string to render as a QR. */
  payload: string;
  /** Merchant name shown to the payer. */
  name: string;
  /** Masked proxy for context, e.g. "+65 •••• 4567". */
  proxy: string;
  /** Brand module colour, e.g. "#4a3428"; null means plain black. */
  color: string | null;
  /** Centre logo URL; null means no logo. */
  logo_url: string | null;
  /** How the client draws the code. */
  style: QrStyle;
};

/** The ways the customer display can draw the PayNow code. */
export type QrStyle = 'plain' | 'logo' | 'halftone' | 'photo';

/** The basket as the customer-facing display draws it. */
export type DisplayCart = {
  items: DisplayLine[];
  total: number;
  savings: number;
  event_name: string | null;
  /** True only on the frame that finishes an order; see the display's thank-you. */
  order_complete: boolean;
  /** Stamped by the server on publish; null until the first basket arrives. */
  updated_at: string | null;
  /** Set by the server when the account configured PayNow and owes something. */
  paynow: DisplayPaynow | null;
};

/** What the till sends; timestamp and QR are added by the server. */
export type DisplayCartInput = Omit<DisplayCart, 'updated_at' | 'paynow'>;

/**
 * Publish the current basket for the customer-facing display.
 *
 * Best-effort: the display is a mirror, so the caller may ignore a rejection
 * rather than let it disturb the till.
 */
export function pushDisplayCart(cart: DisplayCartInput): Promise<void> {
  return request<void>('/api/display/cart', { method: 'POST', body: JSON.stringify(cart) });
}

/**
 * The display's Server-Sent Events endpoint.
 *
 * It carries no credentials in the URL. EventSource cannot set an Authorization
 * header, so the session travels in the HttpOnly cookie that
 * openDisplaySession sets instead -- a token in a query string is recorded by
 * every access log on the way, and stays in the log store afterwards.
 */
export function displayStreamUrl(): string {
  return `${API_BASE_URL}/api/display/stream`;
}

/**
 * Re-mint the display's stream cookie from the session.
 *
 * The cookie carries a short-lived, display-scoped token rather than the
 * session, so the display calls this on a timer and whenever the stream drops
 * instead of relying on one long-lived credential. Credentials are included
 * because the API is a different origin outside production: a same-origin fetch
 * would drop the Set-Cookie entirely.
 */
export function openDisplaySession(): Promise<void> {
  return request<void>('/api/display/session', { method: 'POST', credentials: 'include' });
}

// ------------------------------------------------------------ account settings

export type PaynowSettings = {
  /** "mobile" for a handphone, "uen" for a registered business, or null. */
  type: 'mobile' | 'uen' | null;
  /** Stored form: +65XXXXXXXX for mobile, the UEN for a business. */
  value: string | null;
  /** Name shown to the payer; defaults to the account name. */
  name: string | null;
  configured: boolean;
  /** Brand module colour, e.g. "#4a3428"; null means plain black. */
  color: string | null;
  /** Uploaded logo's image id, for editing. */
  logo_id: number | null;
  /** Logo URL for preview and display; null means no logo. */
  logo_url: string | null;
  /** Chosen render style; null falls back to a logo when one is set. */
  style: QrStyle | null;
};

export function fetchPaynow(): Promise<PaynowSettings> {
  return request<PaynowSettings>('/api/account/paynow', {});
}

/**
 * Build the display payload for settings that are not saved yet.
 *
 * The settings page uses this to render the real QR while the artist edits, so
 * the preview cannot drift from what the customer display will draw.
 */
export function previewPaynow(
  settings: {
    type: 'mobile' | 'uen' | null;
    value?: string | null;
    name?: string | null;
    color?: string | null;
    logo_id?: number | null;
    style?: QrStyle | null;
    amount_cents?: number;
  },
  signal?: AbortSignal
): Promise<DisplayPaynow | null> {
  return request<DisplayPaynow | null>('/api/account/paynow/preview', { method: 'POST', body: JSON.stringify(settings) }, signal);
}

export function savePaynow(settings: {
  type: 'mobile' | 'uen' | null;
  value?: string | null;
  name?: string | null;
  color?: string | null;
  logo_id?: number | null;
  style?: QrStyle | null;
}): Promise<PaynowSettings> {
  return request<PaynowSettings>('/api/account/paynow', { method: 'PUT', body: JSON.stringify(settings) });
}
